import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

type MarkCompleteComplianceDialogProps = {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  isSubmitting: boolean
  i18nNamespace?: 'machineryDirectiveCompliance' | 'craCompliance'
}

export function MarkCompleteComplianceDialog({
  isOpen,
  onClose,
  onConfirm,
  isSubmitting,
  i18nNamespace = 'machineryDirectiveCompliance',
}: MarkCompleteComplianceDialogProps) {
  const { t } = useTranslation(i18nNamespace)
  const [certifyChecked, setCertifyChecked] = useState(false)

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose()
      if (!isSubmitting) {
        setCertifyChecked(false)
      }
    }
  }

  const handleConfirm = () => {
    if (!certifyChecked || isSubmitting) {
      return
    }
    onConfirm()
    setCertifyChecked(false)
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className='sm:max-w-md' showCloseButton={!isSubmitting}>
        <DialogHeader>
          <DialogTitle>{t('markComplete.dialog.title')}</DialogTitle>
        </DialogHeader>
        <div className='space-y-4 py-2'>
          <p className='text-sm text-muted-foreground'>
            {t('markComplete.dialog.description')}
          </p>
          <label className='flex cursor-pointer items-start gap-3 rounded-md border border-input p-3 transition-colors hover:bg-muted/50'>
            <Checkbox
              checked={certifyChecked}
              onCheckedChange={(checked) => {
                setCertifyChecked(checked === true)
              }}
              disabled={isSubmitting}
              className='mt-0.5'
            />
            <span className='text-sm leading-tight font-medium'>
              {t('markComplete.dialog.certifyLabel')}
            </span>
          </label>
        </div>
        <DialogFooter>
          <Button variant='outline' onClick={onClose} disabled={isSubmitting}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!certifyChecked || isSubmitting}
          >
            {isSubmitting
              ? t('markComplete.dialog.submitting')
              : t('markComplete.dialog.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
